/**
 * 3D Text Effect Controls — Schieberegler für Tiefe, Winkel, Farben.
 * Wird sowohl im Desktop-PropertyPanel als auch im Mobile-PropertySheet verwendet.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { fabric } from 'fabric';
import { createGoldGradient, createYellowOrangeGradient, createRedYellowGradient, createBlueGradient, createGreenGradient, createChromeGradient } from '../core/TextEffects3D';

const COLORS = ['#1a1a2e', '#000000', '#2c3e50', '#7b241c', '#1e3a5f', '#1a5c2e', '#4a1a6b', '#333333'];
const NEON_COLORS = ['#00ffff', '#ff00ff', '#39ff14', '#ff6b6b', '#ffd93d', '#00ff88', '#ff4500', '#7b68ee'];

export default function Effect3DControls({ activeObject, canvasManager }) {
  const [, forceUpdate] = useState(0);
  const rerender = useCallback(() => forceUpdate(n => n + 1), []);

  if (!activeObject || !canvasManager?.canvas) return null;

  const t = activeObject.type;
  const is3D = t === 'extrusion3DIText';
  const isOutline = t === 'outlineStackIText';
  const isNeon = t === 'neonIText';
  const isRetro = t === 'retroIText';
  const isCurved = !!activeObject._curvedText;

  if (!is3D && !isOutline && !isNeon && !isRetro && !isCurved) return null;

  const update = (prop, value) => {
    activeObject.set(prop, value);
    activeObject.dirty = true;
    // Update padding for effects that extend beyond bounds
    if (is3D) {
      activeObject.padding = 10;
    } else if (isNeon) {
      activeObject.padding = Math.max(10, activeObject.neonBlur + 10);
    } else if (isRetro) {
      activeObject.padding = Math.max(10, activeObject.longShadowLength + 5);
    }
    activeObject.setCoords();
    canvasManager.canvas.renderAll();
    rerender();
  };

  const rowStyle = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' };
  const labelStyle = { fontSize: '11px', fontWeight: 500, width: '65px', flexShrink: 0, opacity: 0.6, color: 'var(--selenny-text)' };
  const valStyle = { fontSize: '11px', width: '32px', textAlign: 'right', color: 'var(--selenny-text)', opacity: 0.5 };

  return (
    <div>
      <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--selenny-text)' }}>
        {is3D && '3D Extrusion'}
        {isOutline && 'Outline-Effekt'}
        {isNeon && 'Neon-Glow'}
        {isRetro && 'Retro-Schatten'}
      </div>

      {/* ── 3D Extrusion Controls ── */}
      {is3D && (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Tiefe</span>
            <input type="range" min="1" max="25" step="1" value={activeObject.extrusionDepth}
              onChange={(e) => update('extrusionDepth', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.extrusionDepth}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Winkel X</span>
            <input type="range" min="-20" max="20" step="1" value={activeObject.extrusionAngleX}
              onChange={(e) => update('extrusionAngleX', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.extrusionAngleX}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Winkel Y</span>
            <input type="range" min="-20" max="20" step="1" value={activeObject.extrusionAngleY}
              onChange={(e) => update('extrusionAngleY', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.extrusionAngleY}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>3D-Farbe</span>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
              {COLORS.map(c => (
                <button key={c} onClick={() => update('extrusionColor', c)} style={{
                  width: 24, height: 24, borderRadius: '6px', background: c, border: activeObject.extrusionColor === c ? '2px solid #3b82f6' : '1px solid rgba(128,128,128,0.3)',
                  cursor: 'pointer', padding: 0, WebkitTapHighlightColor: 'transparent',
                }} />
              ))}
            </div>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Textfarbe</span>
            <input type="color" value={typeof activeObject.fill === 'string' ? activeObject.fill : '#ff6b35'}
              onChange={(e) => update('fill', e.target.value)}
              style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }} />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <span style={{ ...labelStyle, width: 'auto', display: 'block', marginBottom: '6px' }}>Farbverlauf</span>
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
              {[
                { id: 'fire', label: 'Feuer', fn: createYellowOrangeGradient, bg: 'linear-gradient(to bottom, #ffe44d, #e65100)' },
                { id: 'lava', label: 'Lava', fn: createRedYellowGradient, bg: 'linear-gradient(to bottom, #ff4444, #ffd700)' },
                { id: 'gold', label: 'Gold', fn: createGoldGradient, bg: 'linear-gradient(to bottom, #f5e6a3, #b8860b)' },
                { id: 'chrome', label: 'Silber', fn: createChromeGradient, bg: 'linear-gradient(to bottom, #e8e8e8, #666)' },
                { id: 'ocean', label: 'Ozean', fn: createBlueGradient, bg: 'linear-gradient(to bottom, #60a5fa, #1d4ed8)' },
                { id: 'nature', label: 'Natur', fn: createGreenGradient, bg: 'linear-gradient(to bottom, #4ade80, #15803d)' },
              ].map(g => (
                <button key={g.id} onClick={() => {
                  activeObject.set('fill', g.fn(activeObject.fontSize || 72));
                  activeObject.dirty = true;
                  canvasManager.canvas.renderAll();
                  rerender();
                }} style={{
                  width: 38, height: 38, borderRadius: '8px', border: '1px solid rgba(128,128,128,0.3)',
                  background: g.bg, cursor: 'pointer', padding: 0, WebkitTapHighlightColor: 'transparent',
                }} title={g.label} />
              ))}
              <button onClick={() => { update('fill', '#ff6b35'); }} style={{
                width: 38, height: 38, borderRadius: '8px', border: '1px solid rgba(128,128,128,0.3)',
                background: '#fff', cursor: 'pointer', padding: 0, fontSize: '10px', color: '#999',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }} title="Kein Verlauf">✕</button>
            </div>
          </div>
        </>
      )}

      {/* ── Outline Controls ── */}
      {isOutline && (
        <>
          {(activeObject.outlines || []).map((ol, i) => (
            <div key={i} style={rowStyle}>
              <span style={labelStyle}>{i === 0 ? 'Äußere' : 'Innere'}</span>
              <input type="color" value={ol.color}
                onChange={(e) => {
                  const outlines = [...activeObject.outlines];
                  outlines[i] = { ...outlines[i], color: e.target.value };
                  update('outlines', outlines);
                }}
                style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }} />
              <input type="range" min="1" max="20" step="1" value={ol.width}
                onChange={(e) => {
                  const outlines = [...activeObject.outlines];
                  outlines[i] = { ...outlines[i], width: Number(e.target.value) };
                  update('outlines', outlines);
                }}
                style={{ flex: 1 }} />
              <span style={valStyle}>{ol.width}px</span>
            </div>
          ))}
          <div style={rowStyle}>
            <span style={labelStyle}>Textfarbe</span>
            <input type="color" value={typeof activeObject.fill === 'string' ? activeObject.fill : '#e74c3c'}
              onChange={(e) => update('fill', e.target.value)}
              style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }} />
          </div>
        </>
      )}

      {/* ── Neon Controls ── */}
      {isNeon && (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Intensität</span>
            <input type="range" min="1" max="6" step="1" value={activeObject.neonIntensity}
              onChange={(e) => update('neonIntensity', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.neonIntensity}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Glow</span>
            <input type="range" min="5" max="40" step="1" value={activeObject.neonBlur}
              onChange={(e) => update('neonBlur', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.neonBlur}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Farbe</span>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', flex: 1 }}>
              {NEON_COLORS.map(c => (
                <button key={c} onClick={() => { update('neonColor', c); update('fill', c); }} style={{
                  width: 28, height: 28, borderRadius: '6px', background: c, border: activeObject.neonColor === c ? '2px solid #fff' : '1px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer', padding: 0, boxShadow: `0 0 8px ${c}`, WebkitTapHighlightColor: 'transparent',
                }} />
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Retro Long Shadow Controls ── */}
      {isRetro && (
        <>
          <div style={rowStyle}>
            <span style={labelStyle}>Länge</span>
            <input type="range" min="5" max="60" step="1" value={activeObject.longShadowLength}
              onChange={(e) => update('longShadowLength', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.longShadowLength}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Winkel</span>
            <input type="range" min="0" max="360" step="15" value={activeObject.longShadowAngle}
              onChange={(e) => update('longShadowAngle', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={valStyle}>{activeObject.longShadowAngle}°</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Schatten</span>
            <input type="color" value={activeObject.longShadowColor}
              onChange={(e) => update('longShadowColor', e.target.value)}
              style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }} />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Textfarbe</span>
            <input type="color" value={typeof activeObject.fill === 'string' ? activeObject.fill : '#e74c3c'}
              onChange={(e) => update('fill', e.target.value)}
              style={{ width: '36px', height: '36px', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: 0 }} />
          </div>
        </>
      )}

      {/* ── Curved Text Controls ── */}
      {isCurved && (
        <>
          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--selenny-text)' }}>
            Textbiegung
          </div>
          <div style={{ ...rowStyle, alignItems: 'stretch' }}>
            <span style={{ ...labelStyle, paddingTop: '6px' }}>Text</span>
            <input
              type="text"
              defaultValue={activeObject._curvedText || ''}
              onBlur={(e) => {
                const newText = e.target.value;
                if (newText && newText !== activeObject._curvedText) {
                  canvasManager.updateCurvedText(activeObject, newText, {});
                }
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
              style={{ flex: 1, padding: '6px 10px', border: '1px solid var(--selenny-border)', borderRadius: '6px', background: 'var(--selenny-surface)', color: 'var(--selenny-text)', fontSize: '13px' }}
              placeholder="Text…"
            />
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Biegung</span>
            <input type="range" min="50" max="800" step="2"
              value={Math.round((activeObject._curvedRadiusFactor || 1.0) * 100)}
              onChange={(e) => {
                const rf = Number(e.target.value) / 100;
                canvasManager.updateCurvedText(activeObject, activeObject._curvedText, {
                  radiusFactor: rf,
                });
              }}
              style={{ flex: 1 }} />
            <span style={valStyle}>{Math.round((activeObject._curvedRadiusFactor || 1) * 100)}%</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Abstand</span>
            <input type="range" min="70" max="150" step="1"
              value={Math.round((activeObject._curvedSpacing || 1.0) * 100)}
              onChange={(e) => {
                const sp = Number(e.target.value) / 100;
                canvasManager.updateCurvedText(activeObject, activeObject._curvedText, {
                  spacing: sp,
                });
              }}
              style={{ flex: 1 }} />
            <span style={valStyle}>{Math.round((activeObject._curvedSpacing || 1) * 100)}%</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Form</span>
            <div style={{ display: 'flex', gap: '4px', flex: 1 }}>
              {[
                { id: 'arc-top', label: '⌒' },
                { id: 'arc-bottom', label: '⌣' },
                { id: 'circle', label: '◯' },
                { id: 'wave', label: '∿' },
              ].map(ct => (
                <button key={ct.id} onClick={() => {
                  canvasManager.updateCurvedText(activeObject, activeObject._curvedText, {
                    curveType: ct.id,
                    radiusFactor: activeObject._curvedRadiusFactor,
                    spacing: activeObject._curvedSpacing,
                  });
                }} style={{
                  flex: 1, padding: '8px 4px', borderRadius: '8px', fontSize: '18px',
                  border: activeObject._curvedType === ct.id ? '2px solid #3b82f6' : '1px solid var(--selenny-border)',
                  background: activeObject._curvedType === ct.id ? 'rgba(59,130,246,0.1)' : 'var(--selenny-surface)',
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                  color: 'var(--selenny-text)',
                }}>{ct.label}</button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
